import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssistantEvent,
  AssistantTask,
  AssistantTaskList,
  AssistantToday,
  CreateEventInput,
  CreateTaskInput,
  UpdateEventInput,
  UpdateTaskInput,
} from '@accounting/shared';
import { api } from '../lib/api';
import { assistantKeys } from './useAssistant';

/** Phase 2: tasks, event write-back, Today. Same ['me','assistant',…] key family. */
export const assistantTaskKeys = {
  lists: () => ['me', 'assistant', 'task-lists'] as const,
  tasks: (status: string, connectionIds: string[]) => ['me', 'assistant', 'tasks', status, [...connectionIds].sort().join(',')] as const,
  today: (date: string, tz: string) => ['me', 'assistant', 'today', date, tz] as const,
};

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: assistantKeys.all });
}

// ── Tasks ──────────────────────────────────────────────────────────────────

export function useTaskLists() {
  return useQuery({
    queryKey: assistantTaskKeys.lists(),
    queryFn: () => api.get<{ lists: AssistantTaskList[] }>('/assistant/task-lists').then((r) => r.lists),
    staleTime: 60_000,
  });
}

export function useUpdateTaskList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isSelected }: { id: string; isSelected: boolean }) =>
      api.patch<{ list: AssistantTaskList }>(`/assistant/task-lists/${id}`, { isSelected }).then((r) => r.list),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useTasks(status: 'open' | 'completed' | 'all', connectionIds: string[] = []) {
  return useQuery({
    queryKey: assistantTaskKeys.tasks(status, connectionIds),
    queryFn: () =>
      api
        .get<{ tasks: AssistantTask[] }>('/assistant/tasks', {
          status,
          limit: '500',
          ...(connectionIds.length > 0 ? { connectionIds: connectionIds.join(',') } : {}),
        })
        .then((r) => r.tasks),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.post<{ task: AssistantTask }>('/assistant/tasks', input).then((r) => r.task),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTaskInput & { id: string }) =>
      api.patch<{ task: AssistantTask }>(`/assistant/tasks/${id}`, patch).then((r) => r.task),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assistant/tasks/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Events (write-back) ────────────────────────────────────────────────────

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventInput) => api.post<{ event: AssistantEvent }>('/assistant/events', input).then((r) => r.event),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateEventInput & { id: string }) =>
      api.patch<{ event: AssistantEvent }>(`/assistant/events/${id}`, patch).then((r) => r.event),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assistant/events/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Today ──────────────────────────────────────────────────────────────────

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function useToday(date: string) {
  const tz = browserTimeZone();
  return useQuery({
    queryKey: assistantTaskKeys.today(date, tz),
    queryFn: () => api.get<AssistantToday>('/assistant/today', { date, tz }),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
